import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router';

import { useTypedParams } from '@sorare/routing';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_USERGROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { ChampionshipPage } from '#src/components/championship/ChampionshipPage/index.tsx';

import type {
  ChampionshipUserGroupUserQuery,
  ChampionshipUserGroupUserQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const CHAMPIONSHIP_USERGROUP_USER_QUERY = gql`
  query ChampionshipUserGroupUserQuery(
    $userGroupSlug: String!
    $userSlug: String!
  ) {
    user(slug: $userSlug) {
      slug
      nickname
      ...ChampionshipPage_user
    }
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        membership(userSlug: $userSlug) {
          id
        }
        ...ChampionshipPage_so5UserGroup
      }
    }
  }
  ${ChampionshipPage.fragments.user}
  ${ChampionshipPage.fragments.so5UserGroup}
` as TypedDocumentNode<
  ChampionshipUserGroupUserQuery,
  ChampionshipUserGroupUserQueryVariables
>;

export const loader = queryLoader<PageParams>(
  CHAMPIONSHIP_USERGROUP_USER_QUERY,
  ({ userGroup, user }) => ({
    userGroupSlug: userGroup,
    userSlug: user,
  })
);

const PlayProChampionshipUserGroupUserPage = () => {
  const tabletAndAbove = useIsTabletAndAbove();
  const navigate = useNavigate();
  const { eventType } = useTypedParams<PageParams>();
  const { generateSportContextPath } = useSportContext();

  const { data } = useQueryLoader<ChampionshipUserGroupUserQuery>();

  const { user, so5 } = data || {};

  if (!tabletAndAbove) {
    return (
      <Dialog
        open
        onClose={() =>
          navigate(
            generateSportContextPath(
              ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_USERGROUP,
              {
                params: {
                  eventType: eventType.toLowerCase(),
                  userGroup: so5.so5UserGroup.slug,
                },
              }
            ),
            { replace: true }
          )
        }
        title={<LabelL bold>{user.nickname}</LabelL>}
      >
        <ChampionshipPage so5UserGroup={so5.so5UserGroup} user={user} />
      </Dialog>
    );
  }

  return <ChampionshipPage so5UserGroup={so5.so5UserGroup} user={user} />;
};

export default PlayProChampionshipUserGroupUserPage;
